import { useState } from "react";
import { toast } from "sonner";
import { FileText, Printer, Download, Share2, Mail, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import adminApi from "@/lib/adminApi";

// Abre el contrato guardado en una ventana (opcionalmente lanza impresión). Reutilizable por otros módulos.
export async function openContractWindow(contractId, fmt = "thermal", print = true) {
  try {
    const { data } = await adminApi.get(`/admin/contracts/${contractId}/html`, { params: { fmt } });
    const w = window.open("", "_blank", "width=520,height=800");
    if (!w) { toast.error("Permite las ventanas emergentes para ver/imprimir"); return; }
    w.document.open(); w.document.write(data.html); w.document.close(); w.focus();
    if (print) setTimeout(() => { try { w.print(); } catch (e) {} }, 500);
  } catch (e) { toast.error("No se pudo abrir el contrato"); }
}

const money = (a) => (a == null ? "—" : Number(a) === Math.trunc(Number(a)) ? `$${Number(a).toFixed(0)}` : `$${Number(a).toFixed(2)}`);

// Devuelve datos derivados de la operación para prellenar / mostrar.
function derive(operationType, op) {
  if (operationType === "purchase") {
    const c = op.customer || {};
    return {
      customer: { name: c.name || "", doc: c.doc || "", phone: c.phone || "", email: c.email || "", address: c.address || "" },
      imei: op.imei || "",
      model: op.model, capacity: op.capacity, color: op.color,
      amount: op.price_paid,
    };
  }
  const ct = op.contact || {};
  const amount = op.operation_status === "CASH REALIZADO" && op.cash_price != null ? op.cash_price : op.price;
  return {
    customer: { name: ct.name || "", doc: "", phone: ct.phone || "", email: "", address: "" },
    imei: "",
    model: op.model, capacity: op.capacity, color: op.color,
    amount,
  };
}

export function ContractButton({ operationType, operation, variant = "default", size = "row" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [contract, setContract] = useState(null);
  const base = derive(operationType, operation);
  const [cust, setCust] = useState(base.customer);
  const [imei, setImei] = useState(base.imei);

  const openDialog = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/contracts", { params: { operation_id: operation.id, operation_type: operationType } });
      const existing = (data || []).find((c) => c.operation_id === operation.id);
      if (existing) setContract(existing);
      else setContract(null);
    } catch (e) {
      toast.error("No se pudo cargar el contrato");
    } finally { setLoading(false); }
  };

  const generate = async () => {
    setBusy("gen");
    try {
      const payload = { operation_type: operationType, operation_id: operation.id, customer: { ...cust } };
      if (operationType === "trade_in" && imei) payload.imei = imei;
      const { data } = await adminApi.post("/admin/contracts", payload);
      setContract(data);
      toast.success("Contrato generado y guardado");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar el contrato"); }
    finally { setBusy(""); }
  };

  const doPrint = async (fmt) => {
    setBusy(fmt === "letter" ? "print-letter" : "print-thermal");
    try { await openContractWindow(contract.id, fmt, true); } finally { setBusy(""); }
  };

  const fetchBlob = async () => {
    const res = await adminApi.get(`/admin/contracts/${contract.id}/pdf`, { responseType: "blob" });
    return new Blob([res.data], { type: "application/pdf" });
  };

  const doDownload = async () => {
    setBusy("pdf");
    try {
      const blob = await fetchBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `contrato-${contract.contract_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo generar el PDF"); }
    finally { setBusy(""); }
  };

  const doShare = async () => {
    setBusy("share");
    try {
      const blob = await fetchBlob();
      const file = new File([blob], `contrato-${contract.contract_number}.pdf`, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Contrato ${contract.contract_number}` });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = file.name;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        toast.info("Compartir no disponible en este navegador; se descargó el PDF.");
      }
    } catch (e) { if (e?.name !== "AbortError") toast.error("No se pudo compartir"); }
    finally { setBusy(""); }
  };

  const doEmail = async () => {
    setBusy("email");
    try {
      const { data } = await adminApi.post(`/admin/contracts/${contract.id}/email`);
      toast.success(`Contrato enviado a ${data.to}`);
      setContract({ ...contract, emailed_to: data.to });
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo enviar el correo"); }
    finally { setBusy(""); }
  };

  const triggerCls = size === "row"
    ? "text-xs font-medium text-zinc-700 hover:text-zinc-900 inline-flex items-center gap-1"
    : "h-11 rounded-xl";

  return (
    <>
      {size === "row" ? (
        <button data-testid={`contract-open-${operation.id}`} onClick={openDialog} className={triggerCls}>
          <FileText className="w-3.5 h-3.5" /> Contrato
        </button>
      ) : (
        <Button data-testid={`contract-open-${operation.id}`} onClick={openDialog} variant={variant} className={triggerCls}>
          <FileText className="w-4 h-4 mr-2" /> Generar contrato
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-md max-h-[88vh] overflow-y-auto" data-testid="contract-dialog">
          <DialogHeader>
            <DialogTitle>Contrato de compra-venta</DialogTitle>
            <DialogDescription>
              {operationType === "purchase" ? "Compra directa" : "Trade-In"} · {base.model} {base.capacity ? `· ${base.capacity}` : ""}
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
          ) : !contract ? (
            <div className="space-y-3">
              <p className="text-sm text-zinc-500">Revisa y completa los datos del cliente. No modifican la operación original; se guardan solo en el contrato.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input data-testid="contract-cust-name" placeholder="Nombre completo" value={cust.name} onChange={(e) => setCust({ ...cust, name: e.target.value })} className="h-11 rounded-xl" />
                <Input placeholder="Documento / identificación" value={cust.doc} onChange={(e) => setCust({ ...cust, doc: e.target.value })} className="h-11 rounded-xl" />
                <Input placeholder="Teléfono" value={cust.phone} onChange={(e) => setCust({ ...cust, phone: e.target.value })} className="h-11 rounded-xl" />
                <Input data-testid="contract-cust-email" placeholder="Correo (para enviar por email)" value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })} className="h-11 rounded-xl" />
                <Input placeholder="Dirección (opcional)" value={cust.address} onChange={(e) => setCust({ ...cust, address: e.target.value })} className="h-11 rounded-xl sm:col-span-2" />
                {operationType === "trade_in" && (
                  <Input placeholder="IMEI (opcional)" value={imei} onChange={(e) => setImei(e.target.value)} className="h-11 rounded-xl sm:col-span-2" />
                )}
              </div>
              <div className="rounded-xl bg-zinc-50 p-3 text-sm">
                <div className="flex justify-between"><span className="text-zinc-500">Precio de venta</span><span className="font-semibold">{money(base.amount)} USD</span></div>
                <p className="text-[11px] text-zinc-400 mt-1">Se usa exactamente el monto registrado en la operación. No se recalcula.</p>
              </div>
              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setOpen(false)} className="flex-1 h-11 rounded-xl">Cancelar</Button>
                <Button data-testid="contract-generate-btn" onClick={generate} disabled={busy === "gen"} className="flex-1 h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800">
                  {busy === "gen" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generar contrato"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                Contrato <strong>{contract.contract_number}</strong> guardado como respaldo de la operación.
                {contract.emailed_to && <div className="text-xs mt-1">Enviado a {contract.emailed_to}</div>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="contract-print-thermal-btn" onClick={() => doPrint("thermal")} disabled={busy === "print-thermal"} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800">
                  {busy === "print-thermal" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Printer className="w-4 h-4 mr-2" /> 80 mm</>}
                </Button>
                <Button data-testid="contract-print-letter-btn" onClick={() => doPrint("letter")} disabled={busy === "print-letter"} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800">
                  {busy === "print-letter" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Printer className="w-4 h-4 mr-2" /> Carta</>}
                </Button>
                <Button data-testid="contract-pdf-btn" variant="outline" onClick={doDownload} disabled={busy === "pdf"} className="h-11 rounded-xl">
                  {busy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" /> PDF</>}
                </Button>
                <Button data-testid="contract-share-btn" variant="outline" onClick={doShare} disabled={busy === "share"} className="h-11 rounded-xl">
                  {busy === "share" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Share2 className="w-4 h-4 mr-2" /> Compartir</>}
                </Button>
                <Button data-testid="contract-email-btn" variant="outline" onClick={doEmail} disabled={busy === "email" || !contract.customer?.email} className="h-11 rounded-xl col-span-2">
                  {busy === "email" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-2" /> Enviar por correo</>}
                </Button>
              </div>
              <p className="text-[11px] text-zinc-400">80 mm = impresora térmica · Carta = impresora normal (8.5×11"). El PDF descargado usa tamaño carta. Todo proviene del mismo contrato guardado.</p>
              {!contract.customer?.email && <p className="text-[11px] text-zinc-400">El envío por correo requiere un email del cliente registrado en el contrato.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
